<template>
    <Dialog :header="dialogHeader" v-model:visible="dialogVisible" :modal="true" :closable="false"
            :style="{ width: '90%', maxWidth: '820px' }" :breakpoints="{ '960px': '95vw' }">

        <!-- 1) Contraparte -->
        <div class="field">
            <label><b>{{ isReceivable ? 'Cliente' : 'Proveedor' }}</b></label>
            <AutoComplete v-model="partyModel" :suggestions="partySuggestions" :dropdown="true"
                          field="label" forceSelection @complete="searchParties" @item-select="onPartySelected"
                          :placeholder="isReceivable ? 'Busca un cliente con saldo...' : 'Busca un proveedor con saldo...'"
                          style="width:100%;">
                <template #item="slotProps">
                    <div class="party-option">
                        <div class="party-name">{{ slotProps.item.name }}</div>
                        <div class="party-meta">
                            <span>{{ slotProps.item.identification || 'sin identificación' }}</span>
                            <span class="party-saldo">Saldo ${{ money(slotProps.item.saldo) }} · {{ slotProps.item.count }} doc{{ slotProps.item.count === 1 ? '' : 's' }}</span>
                        </div>
                    </div>
                </template>
            </AutoComplete>
            <small v-if="!selectedParty" class="hint">Elige la contraparte para ver sus documentos pendientes.</small>
        </div>

        <template v-if="selectedParty">
            <!-- 2) Monto a registrar -->
            <div class="field amount-field">
                <label><b>{{ isReceivable ? 'Monto cobrado' : 'Monto pagado' }}</b></label>
                <InputNumber v-model="amount" mode="currency" currency="USD" locale="en-US" :min="0"
                             @input="onAmountInput" style="width: 220px;"/>
                <small class="hint">Se reparte solo (vencidas primero). Puedes ajustar una fila o desmarcar documentos.</small>
            </div>

            <!-- 3) Tabla híbrida -->
            <DataTable :value="rows" class="p-datatable-sm bulk-table" responsiveLayout="scroll" dataKey="id">
                <Column style="width:3rem;">
                    <template #header><i class="pi pi-check" v-tooltip.top="'Incluir'"></i></template>
                    <template #body="{ data: row }">
                        <Checkbox v-model="row.include" :binary="true" @change="onIncludeToggle(row)"/>
                    </template>
                </Column>
                <Column field="number" :header="isReceivable ? 'Factura' : 'Documento'" style="min-width:8rem;">
                    <template #body="{ data: row }">
                        <span style="font-weight:600;">{{ row.number }}</span>
                        <small v-if="row.retentionNumber" style="display:block; color:#777;">{{ row.retentionNumber }}</small>
                    </template>
                </Column>
                <Column header="Emitido" style="min-width:7rem;">
                    <template #body="{ data: row }">
                        <span v-if="row.date">{{ formatDate(row.date) }}</span>
                        <span v-else class="muted">—</span>
                    </template>
                </Column>
                <Column header="Vence" style="min-width:7rem;">
                    <template #body="{ data: row }">
                        <span v-if="row.dueDate">{{ formatDate(row.dueDate) }}</span>
                        <span v-else class="muted">—</span>
                    </template>
                </Column>
                <Column header="Estado" style="min-width:6rem;">
                    <template #body="{ data: row }">
                        <Tag :value="row.state === 'VENCIDO' ? 'Vencido' : 'Vigente'" :severity="row.state === 'VENCIDO' ? 'danger' : 'success'"/>
                    </template>
                </Column>
                <Column header="Pendiente" style="min-width:7rem;" bodyStyle="text-align:right;">
                    <template #body="{ data: row }">${{ money(row.pending) }}</template>
                </Column>
                <Column header="A aplicar" style="min-width:9rem;" bodyStyle="text-align:right;">
                    <template #body="{ data: row }">
                        <InputNumber v-model="row.apply" mode="currency" currency="USD" locale="en-US"
                                     :min="0" :max="row.pending" :disabled="!row.include"
                                     @input="onApplyInput(row, $event)" inputStyle="width:8rem; text-align:right;"/>
                    </template>
                </Column>
                <Column header="Queda" style="min-width:7rem;" bodyStyle="text-align:right;">
                    <template #body="{ data: row }">
                        <span :class="{ 'muted': !row.include }">${{ money(rowRemaining(row)) }}</span>
                    </template>
                </Column>
            </DataTable>

            <!-- 4) Resumen de asignación -->
            <div class="assign-summary">
                <div>
                    <span class="lbl">Asignado</span>
                    <span class="val">${{ money(assigned) }}</span>
                </div>
                <div>
                    <span class="lbl">Sin asignar</span>
                    <span class="val" :class="{ bad: Math.abs(unassigned) >= 0.005 }">${{ money(unassigned) }}</span>
                </div>
                <div v-if="amountNum > totalPendingIncluded + 0.005" class="over-warn">
                    <i class="pi pi-exclamation-triangle"></i>
                    El monto supera el pendiente incluido (${{ money(totalPendingIncluded) }}).
                </div>
            </div>

            <!-- 5) Sección de pago (cuenta/método/comprobante) -->
            <div class="pay-section" v-if="hasAccountability">
                <div class="field grid">
                    <label class="col-12 md:col-4"><b>Método de pago</b></label>
                    <div class="col-12 md:col-8">
                        <Dropdown v-model="currentPaymentMethod" :options="availablePaymentMethods" optionLabel="name"
                                  placeholder="-- Seleccione método --" @change="onPaymentMethodChanged" style="width:100%;"/>
                    </div>
                </div>
                <div class="field grid">
                    <label class="col-12 md:col-4"><b>{{ isReceivable ? 'Cuenta destino' : 'Cuenta origen' }}</b></label>
                    <div class="col-12 md:col-8">
                        <Dropdown v-model="selectedAccount" :options="finantialAccounts" optionLabel="name"
                                  placeholder="-- Seleccione cuenta --" style="width:100%;"/>
                    </div>
                </div>
                <div class="field grid" v-if="shouldShowApprovalNumber">
                    <label class="col-12 md:col-4"><b>Nº Aprobación</b></label>
                    <div class="col-12 md:col-8">
                        <InputText v-model="approvalNumber" placeholder="Número de aprobación del voucher" style="width:100%;"/>
                    </div>
                </div>
                <div class="field grid" v-if="shouldShowProofUpload">
                    <label class="col-12 md:col-4"><b>Comprobante</b></label>
                    <div class="col-12 md:col-8">
                        <FileUpload name="bulkProof" chooseLabel="Seleccionar" :fileLimit="1" :showUploadButton="false"
                                    :showCancelButton="false" accept="image/*" :maxFileSize="10000000" customUpload
                                    @select="onProofAttached">
                            <template #empty><p style="margin:0;">O arrastre el comprobante hasta aquí</p></template>
                        </FileUpload>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <Button label="Cancelar" class="p-button-raised p-button-secondary" @click="$emit('onCancel')"/>
            <Button :label="isReceivable ? 'Registrar cobro' : 'Registrar pago'" icon="pi pi-wallet"
                    class="p-button-raised p-button-success" :disabled="!canSave" :loading="saving" @click="save"/>
        </template>
    </Dialog>
</template>

<script>
export default {
    props: ['isVisible', 'type', 'documents'], // type: 'receivable' | 'payable'
    data() {
        return {
            partyModel: null,
            partySuggestions: [],
            selectedParty: null,
            rows: [],
            amount: null,
            selectedAccount: null,
            currentPaymentMethod: null,
            approvalNumber: '',
            selectedProof: null,
            saving: false,
        };
    },
    computed: {
        dialogVisible: {
            get() { return this.isVisible; },
            set() { /* controlado por el padre; no-op para evitar warnings */ },
        },
        isReceivable() {
            return this.type === 'receivable';
        },
        dialogHeader() {
            return this.isReceivable ? 'Registrar cobro (varias ventas)' : 'Registrar pago (varias compras)';
        },
        amountNum() {
            return Number(this.amount) || 0;
        },
        // Contrapartes distintas con saldo. Se agrupan por la clave estable que
        // emite el backend (`partyKey`: identidad del Customer en ventas, RUC en
        // compras). Agrupar por identificación mezclaba clientes distintos que
        // comparten el comodín 9999999999. Fallback defensivo por si un doc no
        // trae partyKey: identificación real (no comodín) y, si no, el nombre.
        parties() {
            const map = new Map();
            (this.documents || []).forEach(doc => {
                const ident = (doc.partyIdentification || '').trim();
                const name = (doc.party || '').trim();
                const key = doc.partyKey
                    || ((ident && !/^(\d)\1*$/.test(ident)) ? ('ID:' + ident) : ('NM:' + name.toLowerCase()));
                if (!map.has(key)) {
                    map.set(key, { key, name, identification: ident || null, saldo: 0, count: 0, docs: [] });
                }
                const g = map.get(key);
                g.saldo += Number(doc.pending) || 0;
                g.count += 1;
                g.docs.push(doc);
            });
            return Array.from(map.values())
                .map(g => ({ ...g, saldo: Math.round(g.saldo * 100) / 100, label: `${g.name} · ${g.identification || 'sin ID'}` }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        hasAccountability() {
            const user = this.$store.getters.getUser;
            const flag = user && user.settings && user.settings.settings && user.settings.settings.account_enableAutoTransactions;
            return !!(flag && flag.value);
        },
        requiresProofForTransactions() {
            const user = this.$store.getters.getUser;
            const flag = user && user.settings && user.settings.settings && user.settings.settings.account_mustAttachtProofInTransactions;
            return !!(flag && flag.value);
        },
        activeMethodCode() {
            return this.currentPaymentMethod ? this.currentPaymentMethod.code : null;
        },
        moneyType() {
            const code = this.activeMethodCode;
            if (!code) return 'TRANSFER';
            if (code === 'CASH') return 'CASH';
            if (code === 'DEBIT_CARD' || code === 'CREDIT_CARD') return 'CARD';
            return 'TRANSFER';
        },
        finantialAccounts() {
            if (!this.$store.getters.finantialAccounts) return [];
            return this.$store.getters.finantialAccounts.filter(a => a.moneyType === this.moneyType);
        },
        availablePaymentMethods() {
            return [
                { name: 'Efectivo', code: 'CASH' },
                { name: 'Transferencia', code: 'TRANSFER' },
                { name: 'Cheque', code: 'CHECK' },
                { name: 'Tarjeta de Débito', code: 'DEBIT_CARD' },
                { name: 'Tarjeta de Crédito', code: 'CREDIT_CARD' },
            ];
        },
        shouldShowApprovalNumber() {
            return this.activeMethodCode === 'DEBIT_CARD' || this.activeMethodCode === 'CREDIT_CARD';
        },
        shouldShowProofUpload() {
            return this.activeMethodCode === 'TRANSFER' || this.activeMethodCode === 'CHECK';
        },
        includedRows() {
            return this.rows.filter(r => r.include);
        },
        assigned() {
            return this.round2(this.includedRows.reduce((s, r) => s + (Number(r.apply) || 0), 0));
        },
        unassigned() {
            return this.round2(this.amountNum - this.assigned);
        },
        totalPendingIncluded() {
            return this.round2(this.includedRows.reduce((s, r) => s + r.pending, 0));
        },
        canSave() {
            if (this.saving) return false;
            if (!this.selectedParty) return false;
            if (this.amountNum <= 0) return false;
            if (Math.abs(this.unassigned) >= 0.005) return false;          // debe cuadrar
            if (this.amountNum > this.totalPendingIncluded + 0.005) return false;
            const allocs = this.includedRows.filter(r => (Number(r.apply) || 0) > 0);
            if (allocs.length === 0) return false;
            if (allocs.some(r => (Number(r.apply) || 0) > r.pending + 0.005)) return false;
            // Reglas de cuenta/método (solo con contabilidad automática).
            if (this.hasAccountability) {
                if (!this.currentPaymentMethod) return false;
                if (!this.selectedAccount) return false;
                const code = this.activeMethodCode;
                if ((code === 'TRANSFER' || code === 'CHECK') && this.requiresProofForTransactions && !this.selectedProof) return false;
                if ((code === 'DEBIT_CARD' || code === 'CREDIT_CARD') && !this.approvalNumber) return false;
            }
            return true;
        },
    },
    watch: {
        isVisible(newVal) {
            if (newVal) this.reset();
        },
        partyModel(val) {
            // Si el usuario borra el texto, limpiar la selección.
            if (!val || typeof val === 'string') {
                if (!val) { this.selectedParty = null; this.rows = []; this.amount = null; }
            }
        },
    },
    methods: {
        reset() {
            this.partyModel = null;
            this.partySuggestions = [];
            this.selectedParty = null;
            this.rows = [];
            this.amount = null;
            this.selectedAccount = null;
            this.currentPaymentMethod = null;
            this.approvalNumber = '';
            this.selectedProof = null;
            this.saving = false;
        },
        round2(x) {
            return Math.round((Number(x) || 0) * 100) / 100;
        },
        money(v) {
            return (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(raw) {
            if (!raw) return '';
            const d = new Date(raw.toString().replace(' ', 'T'));
            if (isNaN(d.getTime())) return raw.toString();
            return d.toLocaleDateString('es-EC', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        searchParties(event) {
            const q = (event.query || '').trim().toLowerCase();
            const all = this.parties;
            this.partySuggestions = !q
                ? all
                : all.filter(p => p.name.toLowerCase().includes(q) || (p.identification || '').toLowerCase().includes(q));
        },
        onPartySelected(event) {
            const party = event.value;
            this.selectedParty = party;
            // Orden de la cascada: vencidas primero (más antigua→reciente por
            // vencimiento), luego vigentes por vencimiento. Sin fecha al final.
            const sorted = [...party.docs].sort((a, b) => {
                const sa = a.state === 'VENCIDO' ? 0 : 1;
                const sb = b.state === 'VENCIDO' ? 0 : 1;
                if (sa !== sb) return sa - sb;
                const da = a.dueDate || '9999';
                const db = b.dueDate || '9999';
                return da < db ? -1 : da > db ? 1 : 0;
            });
            this.rows = sorted.map(d => ({
                id: d.id,
                number: d.number,
                retentionNumber: d.retentionNumber || null,
                date: d.date,
                dueDate: d.dueDate,
                state: d.state,
                pending: Number(d.pending) || 0,
                include: true,
                manual: false,
                apply: 0,
            }));
            this.amount = null;
        },
        redistribute() {
            // Cascada sobre las filas incluidas, respetando las filas manuales
            // (fijas) y repartiendo el resto en orden hasta agotar el monto.
            let locked = 0;
            this.rows.forEach(r => {
                if (r.include && r.manual) {
                    r.apply = this.clamp(Number(r.apply) || 0, 0, r.pending);
                    locked += r.apply;
                }
            });
            let remaining = this.round2(this.amountNum - locked);
            this.rows.forEach(r => {
                if (!r.include) { r.apply = 0; return; }
                if (r.manual) return;
                if (remaining <= 0) { r.apply = 0; return; }
                const take = Math.min(r.pending, remaining);
                r.apply = this.round2(take);
                remaining = this.round2(remaining - take);
            });
        },
        clamp(x, lo, hi) {
            return Math.max(lo, Math.min(hi, x));
        },
        onAmountInput(event) {
            // Escribir el monto reinicia los ajustes manuales y re-cascadea.
            this.amount = event.value;
            this.rows.forEach(r => { r.manual = false; });
            this.$nextTick(this.redistribute);
        },
        onIncludeToggle(row) {
            if (!row.include) { row.apply = 0; row.manual = false; }
            this.redistribute();
        },
        onApplyInput(row, event) {
            // Edición manual de una fila: queda fija y se recalcula el resto.
            row.apply = event.value || 0;
            row.manual = true;
            this.$nextTick(() => {
                // Re-cascada del remanente sobre las filas NO manuales.
                let locked = 0;
                this.rows.forEach(r => {
                    if (r.include && r.manual) { r.apply = this.clamp(Number(r.apply) || 0, 0, r.pending); locked += r.apply; }
                });
                let remaining = this.round2(this.amountNum - locked);
                this.rows.forEach(r => {
                    if (!r.include || r.manual) return;
                    if (remaining <= 0) { r.apply = 0; return; }
                    const take = Math.min(r.pending, remaining);
                    r.apply = this.round2(take);
                    remaining = this.round2(remaining - take);
                });
            });
        },
        rowRemaining(row) {
            if (!row.include) return row.pending;
            return this.round2(row.pending - (Number(row.apply) || 0));
        },
        onPaymentMethodChanged() {
            this.selectedAccount = null;
        },
        onProofAttached(event) {
            this.selectedProof = event.files[0];
        },
        save() {
            if (!this.canSave) return;
            const allocations = this.includedRows
                .filter(r => (Number(r.apply) || 0) > 0)
                .map(r => ({ documentId: r.id, amount: this.round2(r.apply) }));

            this.$emit('onSave', {
                allocations,
                accountId: this.hasAccountability && this.selectedAccount ? this.selectedAccount.id : null,
                paymentMethod: this.hasAccountability && this.currentPaymentMethod ? this.currentPaymentMethod.code : null,
                approvalNumber: this.approvalNumber || null,
                proof: this.selectedProof || null,
                summary: {
                    total: this.assigned,
                    count: allocations.length,
                    party: this.selectedParty ? this.selectedParty.name : '',
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
.field { margin-bottom: 1rem; }
.field > label { display: block; margin-bottom: 0.4rem; }
.hint { display: block; margin-top: 0.35rem; color: var(--text-color-secondary); font-size: 0.8rem; }
.muted { color: var(--text-color-secondary); }

.party-option { display: flex; flex-direction: column; padding: 0.15rem 0; }
.party-name { font-weight: 600; }
.party-meta { display: flex; justify-content: space-between; gap: 1rem; font-size: 0.8rem; color: var(--text-color-secondary); }
.party-saldo { white-space: nowrap; }

.amount-field label { display: block; }

.bulk-table { margin-top: 0.25rem; }

.assign-summary {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-top: 0.75rem;
    padding: 0.6rem 0.8rem;
    background: rgba(148, 163, 184, 0.1);
    border-radius: 8px;

    .lbl { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-color-secondary); margin-right: 0.4rem; }
    .val { font-size: 1.1rem; font-weight: 700; }
    .val.bad { color: #dc2626; }
    .over-warn { color: #b45309; font-size: 0.85rem; i { margin-right: 0.3rem; } }
}

.pay-section {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--surface-border, #dee2e6);
}
.pay-section .field.grid { margin-bottom: 0.75rem; }
</style>
